// src/pages/LiveScoreboard.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged, signInAnonymously, signInWithCustomToken } from 'firebase/auth';
import { auth, db } from '../firebase';
import { collection, doc, onSnapshot, query } from 'firebase/firestore';
import './GameRecap.css';
import GazetteNewspaper from '../components/GazetteNewspaper';
import GameFanComments from '../components/GameFanComments';
import { getStatsCollectionRoot } from '../utils/teamStatsConfig';

export default function LiveScoreboard({
  teamId,
  initialAuthToken = null,
  recapAsPopup = false,
  enableFanComments = false,
}) {
  const [isStatsVisible, setIsStatsVisible] = useState(true);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedGameId, setSelectedGameId] = useState(null);
  const [games, setGames] = useState([]);
  const [lineup, setLineup] = useState([]);
  const [showPeriodBreakdown, setShowPeriodBreakdown] = useState(true);
  const [showGazettePopup, setShowGazettePopup] = useState(true);
  const [gameData, setGameData] = useState({
    score: { us: 0, them: 0 },
    shots: { us: 0, them: 0 },
    shotsByPeriod: { us: {}, them: {} },
    goalieSaves: 0,
    gameLog: [],
    penalties: [],
    currentPeriod: 1,
  });
  const [selectedSkaterName, setSelectedSkaterName] = useState('');
  const [recap, setRecap] = useState(null);

  const validTeamId = typeof teamId === 'string' && teamId.trim().length > 0;

  const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
  const getEndTime = (g) => {
    const raw = g?.endedAt;
    if (!raw) return null;
    if (typeof raw.toDate === 'function') return raw.toDate().getTime();
    const t = new Date(raw).getTime();
    return Number.isNaN(t) ? null : t;
  };
  const isGameShowable = (g) => {
    if (!g) return false;
    if (!g.archived) return true;
    const endTime = getEndTime(g);
    if (endTime == null) return false;
    return Date.now() - endTime < TWENTY_FOUR_HOURS_MS;
  };

  /** Pick the game to show: prefer live (non-archived) over ended, then most recent by date/end time. */
  const pickBestShowableGame = (showableList) => {
    if (!showableList?.length) return null;
    const live = showableList.filter((g) => !g.archived);
    const candidates = live.length > 0 ? live : showableList;
    return candidates.reduce((p, c) => {
      const dp = getEndTime(p) ?? (p?.details?.date ? new Date(p.details.date).getTime() : 0);
      const dc = getEndTime(c) ?? (c?.details?.date ? new Date(c.details.date).getTime() : 0);
      return dc > dp ? c : p;
    });
  };

  // --- auth (anon allowed) ---
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserId(user.uid);
        setLoading(false);
      } else {
        try {
          if (initialAuthToken) await signInWithCustomToken(auth, initialAuthToken);
          else await signInAnonymously(auth);
        } catch (e) {
          console.error('Auth sign-in failed', e);
        } finally {
          setLoading(false);
        }
      }
    });
    return () => unsub();
  }, [initialAuthToken]);

  // --- firestore refs ---
  const gamesColRef = useMemo(() => {
    if (!validTeamId) return null;
    return collection(db, getStatsCollectionRoot(), teamId, 'games');
  }, [teamId, validTeamId]);

  // --- load games list ---
  useEffect(() => {
    if (!gamesColRef) return;
    const q = query(gamesColRef);
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGames(list);

      // Hide non-archived games explicitly marked live:false; keep archived+live:false so TV/Gladiators TV
      // still shows ended games (recap/Gazette) after coaches open an archived game in the tracker.
      const showable = list.filter((g) => {
        const inLiveWindow =
          isGameShowable(g) && (g.archived === true || g.live !== false);
        // Gladiators TV: keep archived games with a Gazette in rotation even after the 24h "live replay" window.
        if (recapAsPopup && g.archived && g.recap?.headline) return true;
        return inLiveWindow;
      });
      const preferredId = validTeamId && typeof localStorage !== 'undefined' ? localStorage.getItem(`gcg-live-game:${teamId}`) : null;
      // Cleared pin ('') means "no pinned live game" — still auto-pick the best showable game for /live and TV.
      if (preferredId === '') {
        const next = pickBestShowableGame(showable);
        setSelectedGameId(next?.id ?? null);
        return;
      }
      const preferredGame = preferredId ? list.find((g) => g.id === preferredId) : null;
      const preferredInList = preferredGame && preferredGame.live !== false;
      if (preferredInList && preferredId !== selectedGameId) {
        setSelectedGameId(preferredId);
        return;
      }

      const selectedGame = list.find((g) => g.id === selectedGameId);
      const selectedNotLive = selectedGame?.live === false;
      const hasLiveShowable = showable.some((g) => !g.archived);
      const keepSelectionForGazetteTv =
        recapAsPopup &&
        selectedGameId &&
        selectedGame?.archived &&
        selectedGame?.recap?.headline;

      const shouldSwitchAway =
        selectedGameId &&
        !keepSelectionForGazetteTv &&
        (!isGameShowable(selectedGame) ||
          selectedNotLive ||
          (selectedGame?.archived && hasLiveShowable));

      if (shouldSwitchAway) {
        const next = pickBestShowableGame(showable);
        setSelectedGameId(next?.id ?? null);
      } else if (!selectedGameId && showable.length > 0) {
        const best = pickBestShowableGame(showable);
        if (best) setSelectedGameId(best.id);
      }
    });
    return () => unsub();
  }, [gamesColRef, selectedGameId, recapAsPopup]);

  // When admin (or another tab) clears the preferred game, clear selection so this tab shows "No game right now"
  useEffect(() => {
    if (!validTeamId || typeof window === 'undefined') return;
    const key = `gcg-live-game:${teamId}`;
    const handleStorage = (e) => {
      if (e.key === key && (e.newValue === null || e.newValue === '')) {
        setSelectedGameId(null);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [teamId, validTeamId]);

  // --- subscribe to selected game ---
  useEffect(() => {
    if (!gamesColRef || !selectedGameId) return;
    const gameDocRef = doc(gamesColRef, selectedGameId);
    const unsub = onSnapshot(gameDocRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        const loadedGameData = data.gameData || {};
        
        // Ensure shotsByPeriod exists for backwards compatibility
        if (!loadedGameData.shotsByPeriod) {
          loadedGameData.shotsByPeriod = { us: {}, them: {} };
        }
        
        setGameData((prev) => ({ ...prev, ...loadedGameData }));
        setLineup(data.lineup || []);
        setRecap(data.recap || null);
      } else {
        setRecap(null);
      }
    });
    return () => unsub();
  }, [gamesColRef, selectedGameId]);

  const skaters = useMemo(() => lineup.filter((p) => !p.isGoalie), [lineup]);
  const selectedPlayer = useMemo(
    () => (selectedSkaterName && selectedSkaterName !== '__all__' ? lineup.find((p) => p.name === selectedSkaterName) : null),
    [lineup, selectedSkaterName]
  );
  const playerEvents = useMemo(() => {
    if (!selectedPlayer?.name) return [];
    const name = selectedPlayer.name;
    return (gameData.gameLog || []).map((e, i) => {
      if (e.type === 'goal_us') {
        if (e.scorer === name) return { ...e, index: i, kind: 'goal', label: 'Goal' };
        if ([e.assist1, e.assist2, e.assist3].includes(name)) return { ...e, index: i, kind: 'assist', label: 'Assist' };
      }
      if (e.type === 'penalty' && e.player === name) return { ...e, index: i, kind: 'penalty', label: `Penalty — ${e.penaltyType || ''} (${e.duration || ''})` };
      return null;
    }).filter(Boolean);
  }, [gameData.gameLog, selectedPlayer]);

  const goalies = useMemo(() => lineup.filter((p) => p.isGoalie), [lineup]);
  const goaliesWhoPlayed = useMemo(
    () => goalies.filter((p) => (p.saves || 0) > 0 || (p.goalsAllowed || 0) > 0),
    [goalies]
  );
  const totalGoalieSaves = useMemo(() => goalies.reduce((s, p) => s + (p.saves || 0), 0), [goalies]);
  const shotsAgainst = (gameData.goalieSaves ?? totalGoalieSaves) + gameData.score.them;
  const savePctCombined = shotsAgainst > 0 ? ((gameData.goalieSaves ?? totalGoalieSaves) / shotsAgainst) * 100 : 0;

  // Reset Gazette popup to open when game/recap changes (must be before any early return)
  useEffect(() => {
    if (recapAsPopup && recap?.headline) setShowGazettePopup(true);
  }, [recapAsPopup, selectedGameId, recap?.headline]);

  if (!validTeamId) return <div className="container py-4 text-danger">Missing required prop: teamId</div>;
  if (loading) return <div className="container py-5 text-center text-light">Loading…</div>;

  const selectedGame = games.find((g) => g.id === selectedGameId);
  const preferredFromStorage = validTeamId && typeof localStorage !== 'undefined' ? localStorage.getItem(`gcg-live-game:${teamId}`) : null;
  const showPreferredAnyway = selectedGame && selectedGameId === preferredFromStorage;
  const showArchivedGazetteOnTv =
    recapAsPopup && selectedGame?.archived && recap?.headline && selectedGame;
  const currentGame =
    (selectedGame &&
    (isGameShowable(selectedGame) || showPreferredAnyway || showArchivedGazetteOnTv)
      ? selectedGame
      : {}) || {};
  const details = currentGame.details || {};

  const hasRecapPopup = recapAsPopup && currentGame.archived && recap?.headline;

  return (
    <div className="bg-app">
      {/* Gladiator Gazette popup overlay (Gladiators TV only) */}
      {hasRecapPopup && showGazettePopup && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{ zIndex: 1060, background: 'rgba(0,0,0,0.75)', overflow: 'auto' }}
        >
          <div
            className="position-relative rounded shadow-lg bg-dark text-light"
            style={{ maxWidth: 720, width: '100%', maxHeight: '95vh', overflow: 'auto' }}
          >
            <button
              type="button"
              className="position-absolute top-0 end-0 m-2 btn btn-sm btn-outline-light"
              onClick={() => setShowGazettePopup(false)}
              aria-label="Close"
              style={{ zIndex: 1 }}
            >
              ✕
            </button>
            <GazetteNewspaper
              recap={recap}
              dateStr={details.date || recap.generatedAt}
              className="p-4"
              footer={
                <button
                  type="button"
                  className="btn btn-outline-light btn-sm"
                  onClick={() => setShowGazettePopup(false)}
                >
                  Close
                </button>
              }
            />
          </div>
        </div>
      )}

      {/* Button to re-open Gazette when in popup mode and closed */}
      {hasRecapPopup && !showGazettePopup && (
        <div className="container py-2">
          <button type="button" className="btn btn-teal" onClick={() => setShowGazettePopup(true)}>
            View Gladiator Gazette
          </button>
        </div>
      )}

      {enableFanComments && currentGame.id && !currentGame.archived && (
        <div className="container">
          <GameFanComments
            teamId={teamId}
            gameId={currentGame.id}
            gameArchived={!!currentGame.archived}
          />
        </div>
      )}

      <div className="container py-4">
        {/* Header with pulsing LIVE indicator */}
        <div className="d-flex align-items-center justify-content-between mb-4">
          <h1 className="h3 m-0">Live Scoreboard</h1>
          <div className="d-flex align-items-center gap-2">
            {currentGame?.archived ? (
              <span 
                className="badge" 
                style={{ 
                  background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
                  fontSize: '0.9rem',
                  padding: '0.4rem 0.8rem',
                  fontWeight: '700'
                }}
              >
                FINAL
              </span>
            ) : (
              <span 
                className="badge bg-danger" 
                style={{ 
                  animation: 'pulse 2s infinite',
                  fontSize: '0.9rem',
                  padding: '0.4rem 0.8rem'
                }}
              >
                LIVE
              </span>
            )}
            <div className="small text-muted-brand">
              {currentGame?.archived ? 'Game Ended' : 'Auto-updating'}
            </div>
          </div>
        </div>

        {currentGame.id ? (
          <>
            {/* GAME ENDED BANNER */}
            {currentGame.archived && (
              <div className="alert mb-4" style={{
                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                border: '2px solid #fca5a5',
                borderRadius: '1rem',
                padding: '1.5rem',
                textAlign: 'center',
                boxShadow: '0 4px 20px rgba(220, 38, 38, 0.4)'
              }}>
                <div style={{ fontSize: '2rem', fontWeight: '900', color: '#fff', marginBottom: '0.5rem', letterSpacing: '2px' }}>
                  🏁 GAME HAS ENDED 🏁
                </div>
                <div style={{ fontSize: '1.2rem', color: '#fef2f2', fontWeight: '600' }}>
                  Final Score: Gladiators {gameData.score.us} - {details.opponent || 'Opponent'} {gameData.score.them}
                </div>
              </div>
            )}

            {/* ARENA-STYLE SCOREBOARD */}
            <div className="card mb-4" style={{
              background: 'linear-gradient(135deg, #0a0f14 0%, #1a2332 100%)',
              border: '2px solid var(--brand-teal)',
              boxShadow: '0 8px 32px rgba(0, 109, 117, 0.3)',
              borderRadius: '1rem'
            }}>
              <div className="card-body p-4">
                {/* Game Info Bar */}
                <div className="text-center mb-3 pb-3" style={{ borderBottom: '2px solid rgba(180, 151, 90, 0.3)' }}>
                  <div className="d-flex justify-content-center align-items-center gap-4 flex-wrap">
                    <div>
                      <span className="text-muted-brand" style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                        {details.date || '—'}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-brand" style={{ fontSize: '1rem', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
                        📍 {details.location || '—'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Main Scoreboard */}
                <div className="row g-4 align-items-center mb-4">
                  {/* HOME TEAM */}
                  <div className="col-6 col-md-5 order-1">
                    <div className="text-center">
                      <div 
                        className="mb-2" 
                        style={{ 
                          fontSize: 'clamp(0.8rem, 3vw, 1.1rem)', 
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '2px',
                          color: 'var(--brand-gold)'
                        }}
                      >
                        GLADIATORS
                      </div>
                      <div 
                        style={{ 
                          fontSize: 'clamp(3rem, 12vw, 5rem)', 
                          fontWeight: '900',
                          lineHeight: '1',
                          color: 'var(--brand-gold)',
                          textShadow: '0 0 30px rgba(180, 151, 90, 0.5)',
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                      >
                        {gameData.score.us}
                      </div>
                      <div className="mt-2 d-none d-md-block" style={{ fontSize: '0.9rem', color: 'rgba(180, 151, 90, 0.7)' }}>
                        SHOTS: <strong>{gameData.shots.us}</strong>
                      </div>
                      <div className="mt-1 d-md-none" style={{ fontSize: '0.75rem', color: 'rgba(180, 151, 90, 0.7)' }}>
                        <strong>{gameData.shots.us}</strong> SOG
                      </div>
                    </div>
                  </div>

                  {/* AWAY TEAM */}
                  <div className="col-6 col-md-5 order-2 order-md-3">
                    <div className="text-center">
                      <div 
                        className="mb-2" 
                        style={{ 
                          fontSize: 'clamp(0.8rem, 3vw, 1.1rem)', 
                          fontWeight: '700',
                          textTransform: 'uppercase',
                          letterSpacing: '2px',
                          color: '#ffffff'
                        }}
                      >
                        {details.opponent || 'OPPONENT'}
                      </div>
                      <div 
                        style={{ 
                          fontSize: 'clamp(3rem, 12vw, 5rem)', 
                          fontWeight: '900',
                          lineHeight: '1',
                          color: '#ffffff',
                          textShadow: '0 0 30px rgba(255, 255, 255, 0.3)',
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                      >
                        {gameData.score.them}
                      </div>
                      <div className="mt-2 d-none d-md-block" style={{ fontSize: '0.9rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        SHOTS: <strong>{gameData.shots.them}</strong>
                      </div>
                      <div className="mt-1 d-md-none" style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.6)' }}>
                        <strong>{gameData.shots.them}</strong> SOG
                      </div>
                    </div>
                  </div>

                  {/* CENTER - PERIOD - Moves to bottom on mobile */}
                  <div className="col-12 col-md-2 order-3 order-md-2">
                    <div className="text-center">
                      <div 
                        className="badge mb-2" 
                        style={{ 
                          background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                          fontSize: '0.75rem',
                          padding: '0.5rem 1rem',
                          fontWeight: '700',
                          letterSpacing: '1px'
                        }}
                      >
                        {gameData.currentPeriod <= 3 ? 'PERIOD' : 'OVERTIME'}
                      </div>
                      <div 
                        style={{ 
                          fontSize: 'clamp(2rem, 8vw, 3rem)', 
                          fontWeight: '900',
                          color: '#fff',
                          lineHeight: '1'
                        }}
                      >
                        {gameData.currentPeriod <= 3 ? gameData.currentPeriod : 'OT'}
                      </div>
                      {/* Period dots */}
                      <div className="d-flex justify-content-center gap-2 mt-3 align-items-center">
                        {[1, 2, 3].map((n) => (
                          <span
                            key={n}
                            style={{
                              display: 'inline-block',
                              width: 'clamp(12px, 3vw, 16px)',
                              height: 'clamp(12px, 3vw, 16px)',
                              borderRadius: '50%',
                              background: gameData.currentPeriod >= n ? '#dc2626' : 'transparent',
                              border: '2px solid rgba(220, 38, 38, 0.5)',
                              boxShadow: gameData.currentPeriod >= n ? '0 0 10px rgba(220, 38, 38, 0.8)' : 'none'
                            }}
                          />
                        ))}
                        {gameData.currentPeriod >= 4 && (
                          <span className="badge bg-warning ms-2" style={{ fontSize: '0.65rem', padding: '0.25rem 0.5rem', fontWeight: '700' }}>
                            OT
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Goalie Stats Bar — show all goalies who played, or combined total if one goalie */}
                {(goaliesWhoPlayed.length > 0 || (goalies.length === 1 && goalies[0])) && (
                  <div 
                    className="text-center py-3 mt-3" 
                    style={{ 
                      background: 'rgba(0, 109, 117, 0.2)',
                      borderRadius: '0.5rem',
                      border: '1px solid rgba(0, 109, 117, 0.3)'
                    }}
                  >
                    {goaliesWhoPlayed.length > 1 ? (
                      <>
                        <div className="small text-muted-brand mb-2" style={{ fontSize: '0.85rem', fontWeight: '600', letterSpacing: '1px' }}>GOALIES</div>
                        <div className="row g-3 justify-content-center">
                          {goaliesWhoPlayed.map((g) => {
                            const saves = g.saves || 0;
                            const ga = g.goalsAllowed || 0;
                            const shotsAgainstG = saves + ga;
                            const savePct = shotsAgainstG > 0 ? (saves / shotsAgainstG) * 100 : 0;
                            return (
                              <div key={g.name} className="col-12 col-md-6 col-lg-4">
                                <div className="fw-bold" style={{ color: 'var(--brand-gold)', fontSize: '1.2rem' }}>{g.name}</div>
                                <div className="d-flex justify-content-center gap-4 mt-1">
                                  <span><span className="text-muted-brand small">Saves</span> <strong>{saves}</strong></span>
                                  <span><span className="text-muted-brand small">GA</span> <strong>{ga}</strong></span>
                                  <span><span className="text-muted-brand small">SV%</span> <strong>{savePct.toFixed(1)}%</strong></span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div className="mt-2 pt-2 border-top" style={{ borderColor: 'rgba(0, 109, 117, 0.3)' }}>
                          <span className="text-muted-brand small">Total Saves</span> <strong style={{ fontSize: '1.25rem' }}>{gameData.goalieSaves ?? totalGoalieSaves}</strong>
                          <span className="ms-3 text-muted-brand small">Save %</span> <strong style={{ fontSize: '1.25rem' }}>{savePctCombined.toFixed(1)}%</strong>
                        </div>
                      </>
                    ) : (
                      <div className="row">
                        <div className="col-12 col-md-4">
                          <div className="small text-muted-brand mb-1" style={{ fontSize: '0.85rem', fontWeight: '600', letterSpacing: '1px' }}>IN NET</div>
                          <div className="fw-bold" style={{ color: 'var(--brand-gold)', fontSize: '1.4rem' }}>
                            {(goaliesWhoPlayed[0] || goalies[0])?.name}
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="small text-muted-brand mb-1" style={{ fontSize: '0.85rem', fontWeight: '600', letterSpacing: '1px' }}>SAVES</div>
                          <div className="fw-bold text-light" style={{ fontSize: '2rem' }}>
                            {gameData.goalieSaves ?? totalGoalieSaves}
                          </div>
                        </div>
                        <div className="col-6 col-md-4">
                          <div className="small text-muted-brand mb-1" style={{ fontSize: '0.85rem', fontWeight: '600', letterSpacing: '1px' }}>SAVE %</div>
                          <div className="fw-bold text-light" style={{ fontSize: '2rem' }}>
                            {savePctCombined.toFixed(1)}%
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Game Recap (archived games with recap only) - Newspaper Style (inline; skip when recapAsPopup) */}
            {currentGame.archived && recap?.headline && !recapAsPopup && (
              <GazetteNewspaper
                recap={recap}
                dateStr={details.date || recap.generatedAt}
              />
            )}

            {/* GAME LOG */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="section-title h5 mb-3">Game Log</div>
                <ul className="list-group">
                  {currentGame.archived && (
                    <li className="list-group-item" style={{ 
                      background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)', 
                      color: '#fff', 
                      borderColor: '#fca5a5', 
                      padding: '0.75rem',
                      fontWeight: '700',
                      textAlign: 'center',
                      fontSize: '1rem'
                    }}>
                      🏁 FINAL - Game Ended at {currentGame.endedAt ? (typeof currentGame.endedAt.toDate === 'function' ? currentGame.endedAt.toDate() : new Date(currentGame.endedAt)).toLocaleTimeString() : 'N/A'}
                    </li>
                  )}
                  {gameData.gameLog.length === 0 && !currentGame.archived && (
                    <li className="list-group-item" style={{ background: 'var(--brand-card)', color: '#e6e8ec', borderColor: 'var(--brand-border)', padding: '0.75rem' }}>
                      Waiting for updates…
                    </li>
                  )}
                  {gameData.gameLog.slice().reverse().map((e, i) => (
                    <li key={`${e.type}-${i}`} className="list-group-item" style={{ background: 'var(--brand-card)', color: '#e6e8ec', borderColor: 'var(--brand-border)', padding: '0.75rem' }}>
                      {e.period && (
                        <span className="badge bg-danger me-2" style={{ fontWeight: '700', fontSize: '0.75rem' }}>
                          {e.period <= 3 ? `P${e.period}` : 'OT'}
                        </span>
                      )}
                      {e.type === 'goal_us' && (
                        <strong style={{ color: 'var(--brand-gold)', fontSize: '1rem' }}>🚨 [GLADIATORS GOAL]</strong>
                      )}
                      {e.type === 'goal_them' && (
                        <strong className="text-danger" style={{ fontSize: '1rem' }}>🚨 [OPPONENT GOAL]</strong>
                      )}
                      {e.type === 'penalty' && (
                        <strong className="text-warning" style={{ fontSize: '1rem' }}>⚠️ [PENALTY]</strong>
                      )}
                      {e.scorer && <span style={{ fontWeight: '600' }}> {e.scorer} scores!</span>}
                      {e.assist1 && ` (Assist: ${e.assist1})`}
                      {e.assist2 && ` (2nd Assist: ${e.assist2})`}
                      {e.assist3 && ` (3rd Assist: ${e.assist3})`}
                      {e.type === 'penalty' && ` ${e.player} — ${e.penaltyType} (${e.duration})`}
                      <span className="ms-2 small text-muted-brand">at {e.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* PERIOD BREAKDOWN */}
            <div className="card card-glad mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="section-title h5 mb-0">Period Breakdown</div>
                  <button 
                    className="btn btn-neutral btn-sm"
                    onClick={() => setShowPeriodBreakdown(!showPeriodBreakdown)}
                  >
                    {showPeriodBreakdown ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showPeriodBreakdown && (
                  <>
                    <div className="small text-muted-brand mb-2">Shots For (SOF) vs Shots Against (SOA) by Period</div>
                    <div className="table-responsive">
                      <table className="table table-borderless align-middle" style={{ fontSize: '0.95rem', marginBottom: 0, background: 'transparent' }}>
                        <thead>
                          <tr style={{ borderBottom: '2px solid var(--brand-teal)' }}>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.9rem', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>Period</th>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.9rem', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>SOF</th>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.9rem', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>SOA</th>
                            <th style={{ color: 'var(--brand-gold)', fontWeight: '700', textTransform: 'uppercase', fontSize: '0.9rem', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>Diff</th>
                          </tr>
                        </thead>
                        <tbody style={{ background: 'transparent' }}>
                          {[1, 2, 3, 4].map((period) => {
                            const sof = (gameData.shotsByPeriod?.us && gameData.shotsByPeriod.us[period]) || 0;
                            const soa = (gameData.shotsByPeriod?.them && gameData.shotsByPeriod.them[period]) || 0;
                            const diff = sof - soa;
                            const periodLabel = period <= 3 ? `Period ${period}` : 'OT';
                            
                            return (
                              <tr key={period} style={{ background: 'transparent', border: 'none' }}>
                                <td style={{ fontWeight: '600', padding: '0.75rem 0.5rem', color: '#ffffff', background: 'transparent', border: 'none', fontSize: '1rem' }}>{periodLabel}</td>
                                <td style={{ fontWeight: '700', color: 'var(--brand-teal)', fontSize: '1.2rem', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>{sof}</td>
                                <td style={{ fontWeight: '700', color: '#ffffff', fontSize: '1.2rem', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>{soa}</td>
                                <td style={{ padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>
                                  <span className={`badge ${diff > 0 ? 'bg-success' : diff < 0 ? 'bg-danger' : 'bg-secondary'}`} style={{ fontSize: '0.9rem', padding: '0.4rem 0.7rem', fontWeight: '700' }}>
                                    {diff > 0 ? '+' : ''}{diff}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          <tr style={{ borderTop: '3px solid var(--brand-teal)', background: 'transparent', border: 'none' }}>
                            <td style={{ fontWeight: '700', fontSize: '1.15rem', color: 'var(--brand-gold)', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>TOTAL</td>
                            <td style={{ fontWeight: '700', fontSize: '1.4rem', color: 'var(--brand-teal)', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>{gameData.shots.us}</td>
                            <td style={{ fontWeight: '700', fontSize: '1.4rem', color: '#ffffff', padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>{gameData.shots.them}</td>
                            <td style={{ padding: '0.75rem 0.5rem', background: 'transparent', border: 'none' }}>
                              <span className={`badge ${gameData.shots.us - gameData.shots.them > 0 ? 'bg-success' : gameData.shots.us - gameData.shots.them < 0 ? 'bg-danger' : 'bg-secondary'}`} style={{ fontSize: '1rem', padding: '0.5rem 0.8rem', fontWeight: '700' }}>
                                {gameData.shots.us - gameData.shots.them > 0 ? '+' : ''}{gameData.shots.us - gameData.shots.them}
                              </span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* GAME DAY STATS + FIND YOUR SKATER */}
            <div className="card card-glad mb-5">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                  <div className="section-title h5 mb-0">Game Day Stats</div>
                  <button 
                    className="btn btn-neutral btn-sm"
                    onClick={() => setIsStatsVisible(v => !v)}
                  >
                    {isStatsVisible ? "Hide" : "Show"}
                  </button>
                </div>

                {isStatsVisible && (
                  <>
                    <div className="mt-3 mb-3">
                      <label className="small text-muted-brand d-block mb-1" style={{ fontWeight: '600' }}>Find your skater</label>
                      <p className="small text-muted-brand mb-2">Select a player to see their stats and events for this game.</p>
                      <select
                        className="form-select form-select-sm bg-dark text-light border-0"
                        value={selectedSkaterName}
                        onChange={(e) => setSelectedSkaterName(e.target.value)}
                        style={{ maxWidth: '280px' }}
                      >
                        <option value="">Select a skater…</option>
                        {skaters.map((p) => (
                          <option key={p.name} value={p.name}>
                            #{p.number || '—'} · {p.name}
                          </option>
                        ))}
                        {skaters.length > 0 && (
                          <option value="__all__">All skaters</option>
                        )}
                      </select>
                    </div>

                    {selectedPlayer ? (
                      <div className="skater-performance-card rounded p-3 mb-3" style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid var(--brand-border)' }}>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                          <span className="fw-bold" style={{ color: 'var(--brand-gold)', fontSize: '1.1rem' }}>{selectedPlayer.name}</span>
                          {selectedPlayer.number != null && selectedPlayer.number !== '' && (
                            <span className="badge bg-info">#{selectedPlayer.number}</span>
                          )}
                          {selectedPlayer.position && (
                            <span className="badge bg-secondary">{selectedPlayer.position}</span>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-3 mb-3" style={{ fontSize: '0.95rem' }}>
                          <span><strong className="text-muted-brand">G</strong> <span className="fw-bold">{selectedPlayer.goals ?? 0}</span></span>
                          <span><strong className="text-muted-brand">A</strong> <span className="fw-bold">{selectedPlayer.assists ?? 0}</span></span>
                          <span><strong className="text-muted-brand">P</strong> <span className="fw-bold" style={{ color: 'var(--brand-gold)' }}>{selectedPlayer.points ?? 0}</span></span>
                          <span>
                            <strong className="text-muted-brand">+/-</strong>{' '}
                            <span className={`badge ${selectedPlayer.plusMinus > 0 ? 'bg-success' : selectedPlayer.plusMinus < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                              {selectedPlayer.plusMinus > 0 ? '+' : ''}{selectedPlayer.plusMinus ?? 0}
                            </span>
                          </span>
                          <span><strong className="text-muted-brand">Penalties</strong> {selectedPlayer.penalties ?? 0}</span>
                          <span><strong className="text-muted-brand">PIM</strong> {selectedPlayer.pim != null ? Number(selectedPlayer.pim).toFixed(2) : '0.00'}</span>
                        </div>
                        {playerEvents.length > 0 ? (
                          <div>
                            <div className="small text-muted-brand mb-2" style={{ fontWeight: '600' }}>This game</div>
                            <ul className="list-group list-group-flush" style={{ background: 'transparent' }}>
                              {playerEvents.map((ev) => (
                                <li key={ev.index} className="list-group-item d-flex align-items-center gap-2" style={{ background: 'transparent', color: '#e6e8ec', borderColor: 'var(--brand-border)', padding: '0.5rem 0' }}>
                                  {ev.period != null && (
                                    <span className="badge bg-danger" style={{ fontSize: '0.7rem' }}>{ev.period <= 3 ? `P${ev.period}` : 'OT'}</span>
                                  )}
                                  <span className="small text-muted-brand">{ev.time}</span>
                                  {ev.kind === 'goal' && <span style={{ color: 'var(--brand-gold)', fontWeight: '600' }}>Goal</span>}
                                  {ev.kind === 'assist' && <span style={{ color: 'var(--brand-teal)' }}>Assist</span>}
                                  {ev.kind === 'penalty' && <span className="text-warning">{ev.label}</span>}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ) : (
                          <div className="small text-muted-brand">No goals, assists, or penalties recorded this game.</div>
                        )}
                    </div>
                    ) : (
                      <div className="table-responsive mt-2">
                        <table className="table table-dark-glad table-borderless align-middle" style={{ fontSize: '0.875rem' }}>
                          <thead>
                            <tr>
                              {['Name', '#', 'Pos', 'G', 'A', 'P', '+/-', 'Penalties', 'PIM'].map((h) => (
                                <th key={h} style={{ 
                                  fontWeight: '700', 
                                  textTransform: 'uppercase', 
                                  letterSpacing: '0.5px',
                                  color: 'var(--brand-gold)',
                                  fontSize: '0.85rem',
                                  paddingBottom: '0.75rem',
                                  borderBottom: '2px solid var(--brand-teal)'
                                }}>
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {lineup.filter((p) => !p.isGoalie).map((p, idx) => (
                              <tr key={`${p.name}-${idx}`}>
                                <td style={{ fontWeight: '600' }}>{p.name}</td>
                                <td>{p.number}</td>
                                <td>{p.position}</td>
                                <td>{p.goals}</td>
                                <td>{p.assists}</td>
                                <td style={{ fontWeight: '700', color: 'var(--brand-gold)' }}>{p.points}</td>
                                <td>
                                  <span className={`badge ${p.plusMinus > 0 ? 'bg-success' : p.plusMinus < 0 ? 'bg-danger' : 'bg-secondary'}`}>
                                    {p.plusMinus > 0 ? '+' : ''}{p.plusMinus ?? 0}
                                  </span>
                                </td>
                                <td>{p.penalties}</td>
                                <td>{p.pim?.toFixed(2) || '0.00'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

          </>
        ) : (
          <>
            <div className="card card-glad mb-4" style={{ overflow: 'hidden' }}>
              <div className="card-body text-center py-5 px-4">
                <div className="mb-3" style={{ fontSize: '4rem', lineHeight: 1 }}>🏒</div>
                <h2 className="h4 mb-2" style={{ color: 'var(--brand-gold)', fontWeight: '700' }}>No game right now</h2>
                <p className="text-muted-brand mb-3" style={{ fontSize: '1.1rem' }}>
                  The next matchup hasn't dropped yet. Grab a seat—we'll light the lamp soon.
                </p>
                <p className="small text-muted-brand mb-0">
                  Check back for live score updates when the Gladiators hit the ice.
                </p>
              </div>
            </div>
          </>
        )}
      </div>

      {/* CSS Animation for LIVE pulse */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}